import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { openAuthenticatedFile } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageChromeContext';
import { FiEdit, FiSave, FiX, FiClock, FiSearch, FiFilter, FiChevronDown, FiChevronUp, FiUserPlus, FiUserMinus, FiDatabase, FiPlus, FiActivity, FiMail, FiPhone, FiArrowLeft, FiZap, FiRefreshCw, FiUser, FiBriefcase, FiPackage, FiFileText } from 'react-icons/fi';
import './CandidateDetails.css';
import './CRM.css';
import '../components/AiMatchProcessingBar.css';
import LoadingButton from '../components/LoadingButton';
import CrmTimeline, { crmIsFollowUpOverdue } from '../components/CrmTimeline';
import CrmInteractionModal from '../components/CrmInteractionModal';
import IconButton from '../components/IconButton';
import MoreMenu from '../components/MoreMenu';
import Modal from '../components/Modal';
import CandidateServicesPanel from '../components/CandidateServicesPanel';
import CandidatePaymentProfilePanel from '../components/CandidatePaymentProfilePanel';
import CandidateProfileResumeSections, {
  profileHasResumeSections,
} from '../components/CandidateProfileResumeSections';
import ProfileResumeQualityPanel from '../components/ProfileResumeQualityPanel';
import AiMatchProcessingBar from '../components/AiMatchProcessingBar';
import SendTemplateEmailModal from '../components/SendTemplateEmailModal';
import ProfileSupportTickets from '../components/ProfileSupportTickets';
import { MatchScoreBadge, MatchStatusBadge } from '../utils/matchDisplay';
import { isActiveStatus } from '../utils/activeStatus';
import { formatLocalDate, formatLocalDateTime, readCreatedOn, timestampSortValue } from '../utils/formatTimestamp';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import { appAlert, appConfirm } from '../utils/appDialog';
import { emptyProfileResumeSections } from '../utils/resumeExtract';

const PROFILE_TABS = [
  { id: 'profile', label: 'Profile', icon: FiUser },
  { id: 'resume', label: 'Resume', icon: FiFileText },
  { id: 'matches', label: 'Matches', icon: FiBriefcase },
  { id: 'interactions', label: 'Interactions', icon: FiActivity },
  { id: 'packages', label: 'Packages', icon: FiPackage },
  { id: 'history', label: 'History', icon: FiClock, staffOnly: true },
];

function parseStringArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }
  return [];
}

function hasProfileContent(profile) {
  if (!profile || typeof profile !== 'object') return false;
  const fields = [
    'date_of_birth', 'years_of_experience', 'address', 'city', 'state', 'country',
    'zip_code', 'current_job_title', 'job_classification_name', 'current_company',
    'availability', 'linkedin_url', 'portfolio_url', 'github_url',
    'expected_salary_min', 'expected_salary_max', 'work_authorization',
    'summary', 'additional_notes',
  ];
  if (fields.some((field) => profile[field])) return true;
  if (profile.willing_to_relocate) return true;
  if (parseStringArray(profile.preferred_locations).length > 0) return true;
  return profileHasResumeSections(profile);
}

function ensureProfileArrays(profile) {
  const empty = emptyProfileResumeSections();
  if (!profile || typeof profile !== 'object') return { ...empty };
  return {
    ...empty,
    ...profile,
    skills: Array.isArray(profile.skills) ? profile.skills : empty.skills,
    work_experience: Array.isArray(profile.work_experience)
      ? profile.work_experience
      : empty.work_experience,
    education_history: Array.isArray(profile.education_history)
      ? profile.education_history
      : empty.education_history,
    certifications: Array.isArray(profile.certifications)
      ? profile.certifications
      : empty.certifications,
    languages: Array.isArray(profile.languages) ? profile.languages : empty.languages,
    projects: Array.isArray(profile.projects) ? profile.projects : empty.projects,
    links: Array.isArray(profile.links) ? profile.links : empty.links,
    preferred_locations: Array.isArray(profile.preferred_locations)
      ? profile.preferred_locations
      : parseStringArray(profile.preferred_locations),
  };
}

const LOG_ACTION_FILTERS = [
  { value: '', label: 'All', tone: 'neutral' },
  { value: 'create', label: 'Create', tone: 'create' },
  { value: 'update', label: 'Update', tone: 'update' },
  { value: 'delete', label: 'Delete', tone: 'delete' },
  { value: 'view', label: 'View', tone: 'view' },
];

const KNOWN_LOG_ACTIONS = new Set(['create', 'update', 'delete', 'view']);

function logActionClass(action) {
  return KNOWN_LOG_ACTIONS.has(action) ? action : 'unknown';
}

const CandidateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isCandidateMatching, startCandidateMatch } = useAtsMatchRun();
  const candidateMatchRunning = isCandidateMatching(id);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [personalData, setPersonalData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logAction, setLogAction] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showLogCrmModal, setShowLogCrmModal] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const { data: candidate, isLoading, isFetching, isError, error } = useQuery(
    ['candidate', id],
    () => api.get(`/candidates/${id}`).then(res => res.data),
    {
      enabled: Boolean(id),
      keepPreviousData: true,
      refetchOnMount: candidateMatchRunning ? false : true,
    }
  );

  // Keep the form in sync with server data, but never clobber in-progress edits
  // (ATS match completion invalidates this query while the user may be editing).
  useEffect(() => {
    if (!candidate || isEditing) return;
    setProfileData(ensureProfileArrays(candidate.profile || {}));
    setPersonalData({
      first_name: candidate.first_name || '',
      last_name: candidate.last_name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
    });
  }, [candidate, isEditing]);

  usePageTitle(
    candidate
      ? [candidate.first_name, candidate.last_name].filter(Boolean).join(' ').trim() || 'Candidate'
      : 'Candidate'
  );

  // Fetch job roles for dropdown
  const { data: jobRoles = [] } = useQuery(
    ['job-roles'],
    () => api.get('/job-roles').then(res => res.data),
    {
      enabled: isEditing
    }
  );

  // Fetch consultants for assignment (admin only)
  const { data: consultants = [] } = useQuery(
    ['consultants'],
    () => api.get('/users').then(res => res.data.filter(u => u.role === 'consultant' || u.role === 'admin')),
    {
      enabled: user?.role === 'admin'
    }
  );

  // Assignment state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState('');

  // Assign candidate mutation
  const assignMutation = useMutation(
    ({ consultantId }) => api.post(`/candidates/${id}/assign`, { consultant_id: consultantId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
        setShowAssignModal(false);
        setSelectedConsultant('');
      },
    }
  );

  // Unassign candidate mutation
  const unassignMutation = useMutation(
    ({ consultantId }) => api.delete(`/candidates/${id}/assign/${consultantId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
      },
    }
  );

  const handleRematchCandidate = async () => {
    const name = [candidate?.first_name, candidate?.last_name].filter(Boolean).join(' ') || 'this candidate';
    if (
      await appConfirm(
        `Run AI matching for ${name} against active jobs? This uses your ATS match AI settings.`
      )
    ) {
      try {
        await startCandidateMatch(id, { label: name });
      } catch {
        /* provider already alerts */
      }
    }
  };

  const handleAssignSubmit = (e) => {
    e.preventDefault();
    if (selectedConsultant) {
      assignMutation.mutate({
        consultantId: parseInt(selectedConsultant)
      });
    }
  };

  // Build activity logs query params
  const activityLogsParams = useMemo(() => {
    const params = new URLSearchParams();
    const profileId = candidate?.profile?.id;
    if (profileId) {
      params.append('entity_type', 'candidate_profile');
      params.append('entity_id', profileId);
    }
    params.append('limit', showAllLogs ? '100' : '5');
    if (logSearch) params.append('search', logSearch);
    if (logDateFrom) params.append('date_from', logDateFrom);
    if (logDateTo) params.append('date_to', logDateTo);
    if (logAction) params.append('action', logAction);
    return params.toString();
  }, [candidate?.profile?.id, showAllLogs, logSearch, logDateFrom, logDateTo, logAction]);

  // Fetch activity logs
  const { data: activityLogs, isLoading: isLoadingLogs } = useQuery(
    ['activity-logs', 'candidate_profile', candidate?.profile?.id, activityLogsParams],
    () =>
      api.get(`/activity-logs?${activityLogsParams}`)
        .then(res => (Array.isArray(res.data) ? res.data : []))
        .catch(() => []),
    {
      enabled: !!candidate?.profile?.id && (user?.role === 'consultant' || user?.role === 'admin'),
      refetchOnWindowFocus: false,
    }
  );

  const handleClearFilters = () => {
    setLogSearch('');
    setLogDateFrom('');
    setLogDateTo('');
    setLogAction('');
  };

  const hasActiveFilters = logSearch || logDateFrom || logDateTo || logAction;

  const updateProfileMutation = useMutation(
    async ({ personal, profile }) => {
      await api.put(`/users/${id}`, {
        email: personal.email,
        first_name: personal.first_name,
        last_name: personal.last_name,
        phone: personal.phone || null,
      });
      await api.post('/candidate-profiles', { user_id: parseInt(id, 10), ...profile });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
        queryClient.invalidateQueries(['candidates']);
        queryClient.invalidateQueries(['activity-logs']);
        setIsEditing(false);
      }
    }
  );

  const uploadResumeMutation = useMutation(
    async (file) => {
      const formData = new FormData();
      formData.append('resume', file);
      formData.append('autofill_profile', 'true');
      await api.post(`/resumes/upload-for-candidate/${id}`, formData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidate', id]);
        queryClient.invalidateQueries(['candidates']);
        void appAlert('Resume uploaded.');
      },
      onError: (err) => {
        void appAlert(err?.response?.data?.error || err?.message || 'Failed to upload resume');
      },
    }
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArrayChange = (name, value) => {
    setProfileData(prev => ({
      ...prev,
      [name]: value.split(',').map(item => item.trim()).filter(item => item)
    }));
  };

  const syncPersonalFromCandidate = (data) => {
    setPersonalData({
      first_name: data?.first_name || '',
      last_name: data?.last_name || '',
      email: data?.email || '',
      phone: data?.phone || '',
    });
  };

  const handleSave = () => {
    if (!personalData.first_name?.trim() || !personalData.last_name?.trim() || !personalData.email?.trim()) {
      return;
    }
    const {
      id: _profileId,
      user_id: _userId,
      job_classification_name: _className,
      createdon_timestamp: _created,
      modifiedon_timestamp: _modified,
      section_scores: _scores,
      ...profilePayload
    } = profileData;
    updateProfileMutation.mutate({ personal: personalData, profile: profilePayload });
  };

  const handleCancel = () => {
    if (candidate?.profile) {
      setProfileData(ensureProfileArrays(candidate.profile));
    } else {
      setProfileData(ensureProfileArrays({}));
    }
    syncPersonalFromCandidate(candidate);
    updateProfileMutation.reset();
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    syncPersonalFromCandidate(candidate);
    if (candidate?.profile) {
      setProfileData(ensureProfileArrays(candidate.profile));
    } else {
      setProfileData(ensureProfileArrays({}));
    }
    updateProfileMutation.reset();
    setIsEditing(true);
  };

  if (!id) {
    return <div className="error">Invalid candidate ID</div>;
  }

  if (isLoading) {
    return <div className="loading">Loading candidate details...</div>;
  }

  if (isError) {
    const message = error?.response?.data?.error || error?.message || 'Failed to load candidate profile';
    return (
      <div className="candidate-details list-page">
        <div className="candidate-details-card">
          <div className="candidate-details-back-row">
            <IconButton icon={FiArrowLeft} label="Back to candidates" size="sm" onClick={() => navigate('/candidates')} />
            <span className="candidate-details-back-label" aria-hidden>Candidates</span>
          </div>
          <div className="error">{message}</div>
          <Link to="/candidates" className="btn btn-secondary" style={{ marginTop: '1rem' }}>
            Back to Candidates
          </Link>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="candidate-details list-page">
        <div className="candidate-details-card">
          <div className="candidate-details-back-row">
            <IconButton icon={FiArrowLeft} label="Back to candidates" size="sm" onClick={() => navigate('/candidates')} />
            <span className="candidate-details-back-label" aria-hidden>Candidates</span>
          </div>
          <div className="error">Candidate not found</div>
        </div>
      </div>
    );
  }

  const profile = candidate.profile || {};
  const canEdit = user?.role === 'admin' || user?.role === 'consultant' || user?.id === parseInt(id);
  const canViewCrm = user?.role === 'consultant' || user?.role === 'admin';
  const crmInteractions = candidate.crm_interactions || [];
  const crmOpenCount = crmInteractions.filter((i) =>
    ['open', 'pending', 'scheduled'].includes(i.status)
  ).length;
  const crmOverdueCount = crmInteractions.filter(crmIsFollowUpOverdue).length;
  const crmLastContact = crmInteractions[0]?.interaction_date;
  const crmNextFollowUp = crmInteractions
    .filter((i) => i.follow_up_date && !['completed', 'cancelled'].includes(i.status))
    .map((i) => ({ date: new Date(i.follow_up_date), id: i.id }))
    .sort((a, b) => a.date - b.date)[0];

  const isStaff = user?.role === 'admin' || user?.role === 'consultant';
  const visibleTabs = PROFILE_TABS.filter((tab) => !tab.staffOnly || isStaff);

  const heroMenuItems = [
    {
      id: 'edit',
      label: isEditing ? 'Editing…' : 'Edit profile',
      icon: FiEdit,
      hidden: !canEdit || isEditing,
      onSelect: () => {
        handleStartEdit();
        if (activeTab !== 'profile' && activeTab !== 'resume') setActiveTab('profile');
      },
    },
    {
      id: 'email',
      label: 'Send email',
      icon: FiMail,
      hidden: !candidate.email,
      onSelect: () => setShowSendEmail(true),
    },
    {
      id: 'assign',
      label: 'Assign consultant',
      icon: FiUserPlus,
      hidden: user?.role !== 'admin' || Boolean(candidate.consultant),
      onSelect: () => setShowAssignModal(true),
    },
    {
      id: 'unassign',
      label: 'Unassign consultant',
      icon: FiUserMinus,
      danger: true,
      hidden: user?.role !== 'admin' || !candidate.consultant,
      disabled: unassignMutation.isLoading,
      onSelect: async () => {
        if (
          await appConfirm(
            `Unassign ${candidate.consultant.first_name} ${candidate.consultant.last_name} from this candidate?`
          )
        ) {
          unassignMutation.mutate({ consultantId: candidate.consultant.id });
        }
      },
    },
    {
      id: 'log-crm',
      label: 'Log interaction',
      icon: FiPlus,
      hidden: !canViewCrm,
      onSelect: () => {
        setActiveTab('interactions');
        setShowLogCrmModal(true);
      },
    },
    {
      id: 'ai-match',
      label: candidateMatchRunning ? 'AI matching…' : 'Run AI match',
      icon: FiZap,
      hidden: !isStaff,
      disabled: candidateMatchRunning,
      onSelect: () => {
        setActiveTab('matches');
        void handleRematchCandidate();
      },
    },
    {
      id: 'open-crm',
      label: 'Open Interactions',
      icon: FiDatabase,
      hidden: !canViewCrm,
      onSelect: () => navigate(`/crm?candidate=${id}`),
    },
  ];

  return (
    <div className="candidate-details list-page">
      <div className="candidate-details-card candidate-details-hero">
        <div className="candidate-details-header">
          <div className="candidate-details-identity">
            <div className="candidate-details-back-row">
              <IconButton
                icon={FiArrowLeft}
                label="Back to candidates"
                size="sm"
                onClick={() => navigate('/candidates')}
              />
              <span className="candidate-details-back-label" aria-hidden>
                Candidates
              </span>
            </div>
            <h1>{candidate.first_name} {candidate.last_name}</h1>
            {(profile?.current_job_title || profile?.job_classification_name) && (
              <p className="candidate-details-role">
                {profile.current_job_title || profile.job_classification_name}
                {profile.current_company ? ` · ${profile.current_company}` : ''}
              </p>
            )}
            <div className="candidate-details-contact">
              <span className="candidate-details-contact-item">
                <FiMail aria-hidden /> {candidate.email}
              </span>
              {candidate.phone && (
                <span className="candidate-details-contact-item">
                  <FiPhone aria-hidden /> {candidate.phone}
                </span>
              )}
            </div>
            {user?.role === 'admin' && (
              <p className="candidate-details-assignment">
                {candidate.consultant
                  ? `Consultant: ${candidate.consultant.first_name} ${candidate.consultant.last_name}`
                  : 'No consultant assigned'}
              </p>
            )}
          </div>
          <div className="list-page-header-actions candidate-details-actions">
            <span className={`badge badge-${isActiveStatus(candidate.is_active) ? 'success' : 'danger'}`}>
              {isActiveStatus(candidate.is_active) ? 'Active' : 'Inactive'}
            </span>
            <MoreMenu label="Candidate actions" align="end" items={heroMenuItems} />
          </div>
        </div>

        <nav className="candidate-profile-tabs" role="tablist" aria-label="Candidate sections">
          {visibleTabs.map(({ id: tabId, label, icon: Icon }) => (
            <button
              key={tabId}
              type="button"
              role="tab"
              aria-selected={activeTab === tabId}
              className={`candidate-profile-tab${activeTab === tabId ? ' is-active' : ''}`}
              onClick={() => {
                setActiveTab(tabId);
                if (tabId !== 'profile' && tabId !== 'resume' && isEditing) handleCancel();
              }}
            >
              <Icon aria-hidden />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'profile' && (
      <div className="candidate-section candidate-details-card candidate-resume-sheet candidate-profile-workspace">
        <div className="section-header">
          <div>
            <h2>Candidate profile</h2>
            <p className="candidate-profile-lead">
              Contact, career, and preferences for this candidate.
            </p>
          </div>
          {isEditing && (
            <div className="edit-actions">
              <LoadingButton
                className="btn btn-success"
                icon={FiSave}
                loading={updateProfileMutation.isLoading}
                loadingLabel="Saving..."
                onClick={handleSave}
              >
                Save
              </LoadingButton>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <FiX /> Cancel
              </button>
            </div>
          )}
        </div>

        {updateProfileMutation.isError && (
          <div className="error" style={{ marginBottom: '1rem' }}>
            {updateProfileMutation.error?.response?.data?.error
              || updateProfileMutation.error?.message
              || 'Failed to save candidate details'}
          </div>
        )}

        <div className="candidate-profile-main candidate-profile-main--solo">
            {isEditing ? (
              <div className="profile-edit-form">
                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Personal</h3>
                    <p>Name and contact used across ATS and CRM.</p>
                  </header>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        value={personalData.first_name}
                        onChange={handlePersonalChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        value={personalData.last_name}
                        onChange={handlePersonalChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={personalData.email}
                        onChange={handlePersonalChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={personalData.phone}
                        onChange={handlePersonalChange}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={profileData.date_of_birth || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Years of Experience</label>
                      <input
                        type="number"
                        name="years_of_experience"
                        value={profileData.years_of_experience || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={profileData.address || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={profileData.city || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={profileData.state || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={profileData.country || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Zip Code</label>
                      <input
                        type="text"
                        name="zip_code"
                        value={profileData.zip_code || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Career</h3>
                    <p>Role, company, and public professional links.</p>
                  </header>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Job Classification</label>
                      <select
                        name="job_classification"
                        value={profileData.job_classification || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a job classification</option>
                        {jobRoles
                          .filter(role => role.is_active === 1 || role.is_active === true)
                          .sort((a, b) => timestampSortValue(a) - timestampSortValue(b))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Job Title</label>
                      <input
                        type="text"
                        name="current_job_title"
                        value={profileData.current_job_title || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., Senior Software Engineer"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Current Company</label>
                      <input
                        type="text"
                        name="current_company"
                        value={profileData.current_company || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>LinkedIn URL</label>
                      <input
                        type="url"
                        name="linkedin_url"
                        value={profileData.linkedin_url || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Portfolio URL</label>
                      <input
                        type="url"
                        name="portfolio_url"
                        value={profileData.portfolio_url || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>GitHub URL</label>
                      <input
                        type="url"
                        name="github_url"
                        value={profileData.github_url || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Preferences</h3>
                    <p>Availability, authorization, salary, and locations.</p>
                  </header>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Availability</label>
                      <select
                        name="availability"
                        value={profileData.availability || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select availability</option>
                        <option value="available">Available</option>
                        <option value="not-available">Not Available</option>
                        <option value="available-soon">Available Soon</option>
                        <option value="contract-only">Contract Only</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Work Authorization</label>
                      <input
                        type="text"
                        name="work_authorization"
                        value={profileData.work_authorization || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., US Citizen, H1B, etc."
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expected Salary Min</label>
                      <input
                        type="number"
                        name="expected_salary_min"
                        value={profileData.expected_salary_min || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Expected Salary Max</label>
                      <input
                        type="number"
                        name="expected_salary_max"
                        value={profileData.expected_salary_max || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="willing_to_relocate"
                        checked={profileData.willing_to_relocate || false}
                        onChange={handleInputChange}
                      />
                      Willing to Relocate
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Preferred Locations (comma-separated)</label>
                    <input
                      type="text"
                      name="preferred_locations"
                      value={Array.isArray(profileData.preferred_locations)
                        ? profileData.preferred_locations.join(', ')
                        : (profileData.preferred_locations || '')}
                      onChange={(e) => handleArrayChange('preferred_locations', e.target.value)}
                      placeholder="e.g., San Francisco, New York, Remote"
                    />
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Notes</h3>
                    <p>Internal notes — not part of the exported resume.</p>
                  </header>
                  <div className="form-group">
                    <label>Additional Notes</label>
                    <textarea
                      name="additional_notes"
                      value={profileData.additional_notes || ''}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                </section>
              </div>
            ) : (
              <div className="candidate-profile-view">
                {!hasProfileContent(profile) && (
                  <p className="profile-empty-state">
                    No profile information yet.
                    {canEdit ? ' Use Edit profile from the menu to add details.' : ''}
                  </p>
                )}

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Personal</h3>
                  </header>
                  <div className="profile-grid">
                    {profile.date_of_birth && (
                      <div className="profile-item">
                        <strong>Date of Birth</strong>
                        <span>{new Date(profile.date_of_birth).toLocaleDateString()}</span>
                      </div>
                    )}
                    {profile.years_of_experience != null && profile.years_of_experience !== '' && (
                      <div className="profile-item">
                        <strong>Experience</strong>
                        <span>{profile.years_of_experience} years</span>
                      </div>
                    )}
                    {(profile.address || profile.city || profile.state || profile.country) && (
                      <div className="profile-item full-width">
                        <strong>Address</strong>
                        <span>
                          {[profile.address, profile.city, profile.state, profile.country, profile.zip_code]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </div>
                    )}
                    {!profile.date_of_birth
                      && (profile.years_of_experience == null || profile.years_of_experience === '')
                      && !(profile.address || profile.city || profile.state || profile.country) && (
                      <p className="form-hint">No personal details on file.</p>
                    )}
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Career</h3>
                  </header>
                  <div className="profile-grid">
                    {(profile.current_job_title || profile.job_classification_name) && (
                      <div className="profile-item full-width">
                        <strong>Current Position</strong>
                        <span>
                          {profile.current_job_title || profile.job_classification_name}
                          {profile.current_company ? ` at ${profile.current_company}` : ''}
                        </span>
                      </div>
                    )}
                    {profile.linkedin_url && (
                      <div className="profile-item">
                        <strong>LinkedIn</strong>
                        <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer">
                          {profile.linkedin_url}
                        </a>
                      </div>
                    )}
                    {profile.portfolio_url && (
                      <div className="profile-item">
                        <strong>Portfolio</strong>
                        <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer">
                          {profile.portfolio_url}
                        </a>
                      </div>
                    )}
                    {profile.github_url && (
                      <div className="profile-item">
                        <strong>GitHub</strong>
                        <a href={profile.github_url} target="_blank" rel="noopener noreferrer">
                          {profile.github_url}
                        </a>
                      </div>
                    )}
                    {!(profile.current_job_title || profile.job_classification_name)
                      && !profile.linkedin_url
                      && !profile.portfolio_url
                      && !profile.github_url && (
                      <p className="form-hint">No career details on file.</p>
                    )}
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Preferences</h3>
                  </header>
                  <div className="profile-grid">
                    {profile.availability && (
                      <div className="profile-item">
                        <strong>Availability</strong>
                        <span>{profile.availability.replace(/-/g, ' ')}</span>
                      </div>
                    )}
                    {profile.work_authorization && (
                      <div className="profile-item">
                        <strong>Work Authorization</strong>
                        <span>{profile.work_authorization}</span>
                      </div>
                    )}
                    {(profile.expected_salary_min || profile.expected_salary_max) && (
                      <div className="profile-item">
                        <strong>Expected Salary</strong>
                        <span>
                          ${profile.expected_salary_min || 'N/A'} – ${profile.expected_salary_max || 'N/A'}
                        </span>
                      </div>
                    )}
                    {hasProfileContent(profile) && (
                      <div className="profile-item">
                        <strong>Willing to Relocate</strong>
                        <span>{profile.willing_to_relocate ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {profile.preferred_locations && parseStringArray(profile.preferred_locations).length > 0 && (
                      <div className="profile-item full-width">
                        <strong>Preferred Locations</strong>
                        <span>{parseStringArray(profile.preferred_locations).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </section>

                {profile.additional_notes && (
                  <section className="candidate-profile-group">
                    <header className="candidate-profile-group-head">
                      <h3>Notes</h3>
                    </header>
                    <p className="candidate-profile-prose">{profile.additional_notes}</p>
                  </section>
                )}
              </div>
            )}
        </div>
      </div>
      )}

      {activeTab === 'resume' && (
      <div className="candidate-section candidate-details-card candidate-resume-sheet candidate-profile-workspace">
        <div className="section-header">
          <div>
            <h2>Resume</h2>
            <p className="candidate-profile-lead">
              CV content on the left. Scores, suggestions, templates, and export stay on the right.
            </p>
          </div>
          {isEditing && (
            <div className="edit-actions">
              <LoadingButton
                className="btn btn-success"
                icon={FiSave}
                loading={updateProfileMutation.isLoading}
                loadingLabel="Saving..."
                onClick={handleSave}
              >
                Save
              </LoadingButton>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <FiX /> Cancel
              </button>
            </div>
          )}
        </div>

        {updateProfileMutation.isError && (
          <div className="error" style={{ marginBottom: '1rem' }}>
            {updateProfileMutation.error?.response?.data?.error
              || updateProfileMutation.error?.message
              || 'Failed to save candidate details'}
          </div>
        )}

        <div className="candidate-profile-split">
          <div className="candidate-profile-main">
            {isEditing ? (
              <div className="profile-edit-form">
                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Summary</h3>
                    <p>Short professional overview — AI suggestions apply here too.</p>
                  </header>
                  <div className="form-group">
                    <label>Professional Summary</label>
                    <textarea
                      name="summary"
                      value={profileData.summary || ''}
                      onChange={handleInputChange}
                      rows={5}
                    />
                  </div>
                </section>

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Experience &amp; skills</h3>
                    <p>
                      Skills, roles, education, certifications, languages, projects, and links.
                      Import from an uploaded file or autofill from the quality panel.
                    </p>
                  </header>
                  <CandidateProfileResumeSections
                    profile={profileData}
                    editing
                    onChange={setProfileData}
                    resumes={candidate.resumes || []}
                  />
                </section>
              </div>
            ) : (
              <div className="candidate-profile-view">
                {profile.summary && (
                  <section className="candidate-profile-group">
                    <header className="candidate-profile-group-head">
                      <h3>Summary</h3>
                    </header>
                    <p className="candidate-profile-prose">{profile.summary}</p>
                  </section>
                )}

                <section className="candidate-profile-group">
                  <header className="candidate-profile-group-head">
                    <h3>Experience &amp; skills</h3>
                  </header>
                  <CandidateProfileResumeSections profile={ensureProfileArrays(profile)} editing={false} />
                </section>
              </div>
            )}

            <section className="candidate-profile-group candidate-resume-files">
              <header className="candidate-profile-group-head">
                <h3>Uploaded resumes</h3>
                <p>{candidate.resumes ? candidate.resumes.length : 0} of 3 files</p>
              </header>

              {canEdit && (candidate.resumes?.length || 0) < 3 && (
                <div className="candidate-resume-upload">
                  <label className="btn btn-secondary btn-sm candidate-resume-upload-btn">
                    {uploadResumeMutation.isLoading ? 'Uploading…' : 'Upload resume'}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      hidden
                      disabled={uploadResumeMutation.isLoading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) uploadResumeMutation.mutate(file);
                      }}
                    />
                  </label>
                  <p className="form-hint">PDF, DOC, or DOCX up to 5MB. Upload can autofill resume sections.</p>
                </div>
              )}

              {candidate.resumes && candidate.resumes.length > 0 ? (
                <div className="resumes-list">
                  {candidate.resumes.map((resume) => {
                    const skills = parseStringArray(resume.skills);
                    return (
                    <div key={resume.id} className="resume-item">
                      <div className="resume-item-header">
                        <h4>{resume.file_name || `Resume ${resume.id}`}</h4>
                        <span className="resume-meta">
                          {resume.file_size && (
                            <span>{(resume.file_size / 1024).toFixed(2)} KB</span>
                          )}
                          {(resume.createdon_timestamp || resume.uploaded_at) && (
                            <span> • Uploaded: {formatLocalDate(readCreatedOn(resume))}</span>
                          )}
                        </span>
                      </div>
                      {skills.length > 0 && (
                        <div className="skills-list">
                          <strong>Skills:</strong>
                          {skills.map((skill, idx) => (
                            <span key={idx} className="skill-tag">{skill}</span>
                          ))}
                        </div>
                      )}
                      {resume.experience_years && (
                        <p><strong>Experience:</strong> {resume.experience_years} years</p>
                      )}
                      {resume.education && (
                        <p><strong>Education:</strong> {resume.education}</p>
                      )}
                      {resume.summary && (
                        <p><strong>Summary:</strong> {resume.summary}</p>
                      )}
                      {resume.file_path && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: '10px', display: 'inline-block' }}
                          onClick={async () => {
                            try {
                              await openAuthenticatedFile(`/resumes/${resume.id}/download`);
                            } catch {
                              void appAlert('Failed to download resume');
                            }
                          }}
                        >
                          Download file
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="form-hint">No resumes uploaded yet.</p>
              )}
            </section>
          </div>

          <aside className="candidate-profile-ai" aria-label="Resume quality and export">
            <ProfileResumeQualityPanel
              layout="sidebar"
              userId={candidate.id}
              sectionScores={
                isEditing
                  ? (profileData.section_scores || profile?.section_scores)
                  : profile?.section_scores
              }
              profile={isEditing ? profileData : ensureProfileArrays(profile)}
              editing={isEditing}
              canEdit={canEdit}
              onProfileChange={(next) => {
                if (canEdit) {
                  if (!isEditing) {
                    syncPersonalFromCandidate(candidate);
                    updateProfileMutation.reset();
                    setIsEditing(true);
                  }
                  setProfileData(ensureProfileArrays(next));
                }
              }}
              onScoresChange={(scores) => {
                if (isEditing) {
                  setProfileData((prev) => ({ ...prev, section_scores: scores }));
                } else {
                  queryClient.invalidateQueries(['candidate', id]);
                }
              }}
            />
          </aside>
        </div>
      </div>
      )}

      {activeTab === 'matches' && (
      <div className="candidate-section candidate-details-card" aria-busy={candidateMatchRunning || undefined}>
        <div className="candidate-match-toolbar">
          <h2>Job matches</h2>
          {isStaff && (
            <div className="candidate-match-toolbar__actions">
              <IconButton
                icon={FiRefreshCw}
                label="Refresh candidate"
                size="sm"
                loading={isFetching || candidateMatchRunning}
                onClick={() => queryClient.invalidateQueries(['candidate', id])}
              />
              <LoadingButton
                className="btn btn-sm btn-primary"
                icon={FiZap}
                loading={candidateMatchRunning}
                loadingLabel="AI matching…"
                onClick={handleRematchCandidate}
                title="AI match this candidate against active jobs"
              >
                AI match
              </LoadingButton>
            </div>
          )}
        </div>
        <AiMatchProcessingBar
          matching={candidateMatchRunning}
          refreshing={isFetching && !candidateMatchRunning}
          label={
            candidateMatchRunning
              ? 'AI matching in progress — scoring this candidate against active jobs…'
              : undefined
          }
        />
        {candidate.matches && candidate.matches.length > 0 ? (
          <div className={`matches-list ${candidateMatchRunning ? 'is-matching' : ''}`}>
            {candidate.matches.map((match) => (
              <div
                key={match.id}
                className={`match-item ${candidateMatchRunning ? 'is-processing' : ''}`}
              >
                <h4>
                  {match.job_id ? (
                    <Link to={`/jobs/${match.job_id}`}>{match.title}</Link>
                  ) : (
                    match.title
                  )}
                </h4>
                <p>Company: {match.company}</p>
                <div className="match-item-score-row">
                  <span>Match Score:</span>
                  <MatchScoreBadge score={match.match_score} />
                  <MatchStatusBadge status={match.status} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="form-hint">No matches yet. Run AI match to score this candidate against active jobs.</p>
        )}
      </div>
      )}

      {activeTab === 'packages' && (
        <>
          <CandidateServicesPanel
            candidateId={id}
            canManagePayments={isStaff}
          />
          <CandidatePaymentProfilePanel
            candidateId={id}
            candidate={candidate}
            canEdit={isStaff}
          />
        </>
      )}

      {activeTab === 'interactions' && (
      <div className="candidate-section candidate-section-crm candidate-details-card">
        <div className="candidate-section-crm-header">
          <h2>
            <FiActivity aria-hidden /> Interactions
          </h2>
          {canViewCrm && (
            <div className="candidate-crm-header-actions page-toolbar">
              <MoreMenu
                label="Interaction actions"
                items={[
                  {
                    id: 'log',
                    label: 'Log interaction',
                    icon: FiPlus,
                    onSelect: () => setShowLogCrmModal(true),
                  },
                  {
                    id: 'open-crm',
                    label: 'Open Interactions workspace',
                    icon: FiDatabase,
                    onSelect: () => navigate(`/crm?candidate=${id}`),
                  },
                ]}
              />
            </div>
          )}
        </div>

        {crmInteractions.length > 0 ? (
          <>
            <div className="candidate-crm-summary">
              <div className="candidate-crm-summary__stat">
                <span className="candidate-crm-summary__label">Total</span>
                <strong>{crmInteractions.length}</strong>
              </div>
              <div className="candidate-crm-summary__stat">
                <span className="candidate-crm-summary__label">Open</span>
                <strong>{crmOpenCount}</strong>
              </div>
              {crmOverdueCount > 0 && (
                <div className="candidate-crm-summary__stat candidate-crm-summary__stat--warn">
                  <span className="candidate-crm-summary__label">Overdue follow-ups</span>
                  <strong>{crmOverdueCount}</strong>
                </div>
              )}
              {crmLastContact && (
                <div className="candidate-crm-summary__stat">
                  <span className="candidate-crm-summary__label">Last contact</span>
                  <strong>
                    {formatLocalDate(crmLastContact, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </strong>
                </div>
              )}
              {crmNextFollowUp && (
                <div className={`candidate-crm-summary__stat${crmIsFollowUpOverdue(crmInteractions.find((i) => i.id === crmNextFollowUp.id)) ? ' candidate-crm-summary__stat--warn' : ''}`}>
                  <span className="candidate-crm-summary__label">Next follow-up</span>
                  <strong>
                    {formatLocalDate(crmNextFollowUp.date, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </strong>
                </div>
              )}
            </div>

            <CrmTimeline
              items={crmInteractions}
              showCandidateName={false}
              showConsultant
            />
          </>
        ) : (
          <div className="candidate-crm-empty-state">
            <p className="candidate-crm-empty">No interactions for this candidate yet.</p>
            {canViewCrm && (
              <IconButton
                icon={FiPlus}
                label="Log first interaction"
                size="sm"
                onClick={() => setShowLogCrmModal(true)}
              />
            )}
          </div>
        )}
      </div>
      )}

      {activeTab === 'history' && isStaff && (
        <>
        <ProfileSupportTickets
          email={candidate?.email}
          entityType="user"
          entityId={candidate?.id}
          limit={5}
        />

        {(candidate?.profile?.id || candidate?.id) && (
        <div className="candidate-section candidate-details-card activity-history">
          <div className="activity-history-header">
            <h2>
              <FiClock /> Activity history
            </h2>
            <div className="activity-history-controls">
              <MoreMenu
                label="History options"
                items={[
                  {
                    id: 'filters',
                    label: showFilters ? 'Hide filters' : 'Show filters',
                    icon: FiFilter,
                    onSelect: () => setShowFilters(!showFilters),
                  },
                  {
                    id: 'show-all',
                    label: 'Show all',
                    icon: FiChevronDown,
                    hidden: showAllLogs || !(activityLogs && activityLogs.length >= 5),
                    onSelect: () => setShowAllLogs(true),
                  },
                  {
                    id: 'show-less',
                    label: 'Show less',
                    icon: FiChevronUp,
                    hidden: !showAllLogs,
                    onSelect: () => setShowAllLogs(false),
                  },
                ]}
              />
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="activity-filters">
              <div className="filter-row">
                <div className="filter-group">
                  <label>
                    <FiSearch /> Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search by description, field, or user..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>Action Type</label>
                  <div className="activity-action-filters">
                    {LOG_ACTION_FILTERS.map(({ value, label, tone }) => (
                      <button
                        key={value || 'all'}
                        type="button"
                        className={`activity-filter-btn activity-filter-btn--${tone}${logAction === value ? ' active' : ''}`}
                        onClick={() => setLogAction(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Date From</label>
                  <input
                    type="date"
                    value={logDateFrom}
                    onChange={(e) => setLogDateFrom(e.target.value)}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label>Date To</label>
                  <input
                    type="date"
                    value={logDateTo}
                    onChange={(e) => setLogDateTo(e.target.value)}
                    className="filter-input"
                  />
                </div>
                {hasActiveFilters && (
                  <div className="filter-group">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleClearFilters}
                      style={{ marginTop: '24px' }}
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isLoadingLogs ? (
            <p>Loading activity history...</p>
          ) : activityLogs && activityLogs.length > 0 ? (
            <>
              <div className="activity-stats">
                <span className="activity-count">
                  Showing {activityLogs.length} log{activityLogs.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ' (filtered)'}
                </span>
              </div>
              <div className="activity-list">
                {activityLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`activity-item activity-item--${logActionClass(log.action)}`}
                  >
                    <div className="activity-header">
                      <div className="activity-action">
                        <span className={`activity-badge activity-${logActionClass(log.action)}`}>
                          {log.action}
                        </span>
                        {log.description && (
                          <span className="activity-description">{log.description}</span>
                        )}
                      </div>
                      <div className="activity-meta">
                        {log.user_name && (
                          <span className="activity-user">{log.user_name}</span>
                        )}
                        <span className="activity-time">
                          {formatLocalDateTime(readCreatedOn(log))}
                        </span>
                      </div>
                    </div>
                    {log.field_name && (
                      <div className="activity-change">
                        <strong>{log.field_name}:</strong>
                        <span className="change-old">{log.old_value || '(empty)'}</span>
                        <span className="change-arrow">→</span>
                        <span className="change-new">{log.new_value || '(empty)'}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!showAllLogs && activityLogs.length === 5 && (
                <div className="activity-more-indicator">
                  <p>Showing 5 most recent logs. Use History options → Show all for more.</p>
                </div>
              )}
            </>
          ) : (
            <p>No activity history available{hasActiveFilters && ' (try adjusting your filters)'}</p>
          )}
        </div>
        )}
        </>
      )}

      <Modal
        open={showAssignModal && user?.role === 'admin'}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedConsultant('');
        }}
        ariaLabel="Assign candidate to consultant"
      >
        <h2>Assign Candidate to Consultant</h2>
        <p className="form-hint">
          Assign <strong>{candidate.first_name} {candidate.last_name}</strong> to a consultant
        </p>
        <form onSubmit={handleAssignSubmit}>
              <div className="form-group">
                <label>Select Consultant</label>
                <select
                  value={selectedConsultant}
                  onChange={(e) => setSelectedConsultant(e.target.value)}
                  required
                >
                  <option value="">Choose a consultant...</option>
                  {consultants.map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.first_name} {consultant.last_name} ({consultant.email}) - {consultant.role}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedConsultant('');
                  }}
                >
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  className="btn btn-success"
                  icon={FiUserPlus}
                  loading={assignMutation.isLoading}
                  loadingLabel="Assigning..."
                  disabled={!selectedConsultant}
                >
                  Assign
                </LoadingButton>
              </div>
            </form>
      </Modal>

      {canViewCrm && (
        <CrmInteractionModal
          open={showLogCrmModal}
          onClose={() => setShowLogCrmModal(false)}
          candidateId={id}
          lockCandidate
          candidateLabel={
            candidate
              ? `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim()
              : ''
          }
        />
      )}

      <SendTemplateEmailModal
        open={showSendEmail && Boolean(candidate)}
        onClose={() => setShowSendEmail(false)}
        recipient={
          candidate
            ? {
                userId: candidate.id,
                email: candidate.email,
                firstName: candidate.first_name,
                lastName: candidate.last_name,
                displayName: `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
              }
            : null
        }
      />
    </div>
  );
};

export default CandidateDetails;
